import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ClipboardCheck, CheckCircle2, AlertTriangle, Save, Loader2 } from "lucide-react";

const monthNow = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const today = () => new Date().toISOString().slice(0, 10);
const trDay = (iso) => {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", weekday: "short" });
  } catch { return iso; }
};

export default function FieldTasks() {
  const [month, setMonth] = useState(monthNow());
  const [tasks, setTasks] = useState([]);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingDate, setSavingDate] = useState(null);
  const [expandedDate, setExpandedDate] = useState(today());
  // drafts[date] = { task_1..4: "resolved"|"open", task_1_note..: "", notes: "" }
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: cfg }, { data: lst }] = await Promise.all([
        api.get("/field-supervisors/config"),
        api.get("/field-supervisors/checklist", { params: { month } }),
      ]);
      setTasks(cfg.tasks || []);
      setDays(lst.days || []);
      const d = {};
      (lst.days || []).forEach((row) => {
        d[row.date] = {
          task_1: row.task_1, task_2: row.task_2, task_3: row.task_3, task_4: row.task_4,
          task_1_note: row.task_1_note || "", task_2_note: row.task_2_note || "",
          task_3_note: row.task_3_note || "", task_4_note: row.task_4_note || "",
          notes: row.notes || "",
        };
      });
      setDrafts(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Yüklenemedi");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const isDone = (st) => st === "resolved";
  const setTaskStatus = (date, i, done) => {
    setDrafts((prev) => ({
      ...prev,
      [date]: { ...(prev[date] || {}), [`task_${i}`]: done ? "resolved" : "open" },
    }));
  };
  const setTaskNote = (date, i, val) => {
    setDrafts((prev) => ({
      ...prev,
      [date]: { ...(prev[date] || {}), [`task_${i}_note`]: val },
    }));
  };
  const setDayNote = (date, val) => {
    setDrafts((prev) => ({ ...prev, [date]: { ...(prev[date] || {}), notes: val } }));
  };

  const saveDay = async (date) => {
    const dr = drafts[date] || {};
    // Aksaklıkta sebep zorunlu → frontend guard (backend de doğrular)
    for (const i of [1, 2, 3, 4]) {
      const st = dr[`task_${i}`] || "open";
      const note = (dr[`task_${i}_note`] || "").trim();
      if (st !== "resolved" && !note) {
        toast.error(`${i}. görev aksaklıklı; sebep yazmak zorunlu.`);
        return;
      }
    }
    setSavingDate(date);
    try {
      await api.post("/field-supervisors/checklist", {
        date,
        task_1: dr.task_1 || "open", task_2: dr.task_2 || "open",
        task_3: dr.task_3 || "open", task_4: dr.task_4 || "open",
        task_1_note: dr.task_1_note || "", task_2_note: dr.task_2_note || "",
        task_3_note: dr.task_3_note || "", task_4_note: dr.task_4_note || "",
        notes: dr.notes || "",
      });
      toast.success(`${trDay(date)} kaydedildi`);
      await load();
      setExpandedDate(date);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi");
    } finally { setSavingDate(null); }
  };

  const summary = useMemo(() => {
    const total = days.length;
    const clean = days.filter((r) => [1, 2, 3, 4].every((i) => (drafts[r.date] || r)[`task_${i}`] === "resolved")).length;
    const withIssue = days.filter((r) => [1, 2, 3, 4].some((i) => {
      const s = (drafts[r.date] || r)[`task_${i}`];
      return s && s !== "resolved";
    })).length;
    const empty = total - clean - withIssue;
    return { total, clean, withIssue, empty };
  }, [days, drafts]);

  return (
    <div className="space-y-5" data-testid="field-tasks-page">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--y-muted)] font-mono">Saha Sorumluları</div>
          <h1 className="mt-1 font-heading font-bold text-2xl lg:text-3xl text-[color:var(--y-text)]">Günlük Görevler</h1>
          <p className="mt-1 text-sm text-[color:var(--y-muted)]">Her gün için 4 kontrol maddesi. Aksaklıkta sebep yazmak zorunludur.</p>
        </div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 w-[160px] font-mono" data-testid="ft-month" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="ft-summary">
        <MiniStat label="Toplam Gün" value={summary.total} />
        <MiniStat label="Sorunsuz Gün" value={summary.clean} tone="ok" />
        <MiniStat label="Aksaklıklı Gün" value={summary.withIssue} tone="warn" />
        <MiniStat label="Girilmemiş Gün" value={summary.empty} tone="mute" />
      </div>

      {loading && <div className="text-sm text-[color:var(--y-muted)]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Yükleniyor...</div>}

      <div className="space-y-2">
        {days.map((row) => {
          const dr = drafts[row.date] || row;
          const isExpanded = expandedDate === row.date;
          const clean = [1, 2, 3, 4].every((i) => dr[`task_${i}`] === "resolved");
          const anyPending = [1, 2, 3, 4].some((i) => dr[`task_${i}`] && dr[`task_${i}`] !== "resolved");
          return (
            <Card key={row.date} className={`${anyPending && !clean ? "border-orange-400/40" : clean ? "border-emerald-400/40" : ""}`} data-testid={`ft-day-${row.date}`}>
              <button
                type="button"
                onClick={() => setExpandedDate(isExpanded ? null : row.date)}
                className="w-full flex items-center justify-between p-3 text-left"
                data-testid={`ft-day-toggle-${row.date}`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-2.5 h-2.5 rounded-full ${clean ? "bg-emerald-500" : anyPending ? "bg-orange-500" : "bg-slate-400"}`} />
                  <div>
                    <div className="font-mono font-semibold text-[color:var(--y-text)]">{trDay(row.date)}</div>
                    <div className="text-[11px] text-[color:var(--y-muted)] font-mono">{row.date}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {[1, 2, 3, 4].map((i) => (
                    <span key={i} className={`w-6 h-6 rounded-full inline-flex items-center justify-center border ${
                      dr[`task_${i}`] === "resolved" ? "bg-emerald-500/20 border-emerald-500 text-emerald-300"
                      : dr[`task_${i}`] && dr[`task_${i}`] !== "open" ? "bg-orange-500/20 border-orange-500 text-orange-300"
                      : "border-[color:var(--y-border)] text-[color:var(--y-muted)]"}`}>
                      {i}
                    </span>
                  ))}
                </div>
              </button>
              {isExpanded && (
                <CardContent className="pt-0 pb-4 space-y-3 border-t border-[color:var(--y-border)]">
                  {tasks.map((t) => {
                    const done = isDone(dr[`task_${t.id}`]);
                    return (
                      <div key={t.id} className="p-3 rounded-md" style={{ background: "var(--y-surface-2)" }} data-testid={`ft-task-${row.date}-${t.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-xs uppercase font-mono text-[color:var(--y-muted)]">{t.id}. Görev</div>
                            <div className="text-sm font-medium text-[color:var(--y-text)] mt-0.5">{t.label}</div>
                            <div className="text-[11px] text-[color:var(--y-muted)] mt-0.5">{t.tooltip}</div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              type="button" size="sm"
                              variant={done ? "default" : "outline"}
                              className={done ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
                              onClick={() => setTaskStatus(row.date, t.id, true)}
                              data-testid={`ft-task-ok-${row.date}-${t.id}`}
                            ><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Sorunsuz</Button>
                            <Button
                              type="button" size="sm"
                              variant={!done && dr[`task_${t.id}`] ? "default" : "outline"}
                              className={!done && dr[`task_${t.id}`] ? "bg-orange-600 hover:bg-orange-700 text-white" : ""}
                              onClick={() => setTaskStatus(row.date, t.id, false)}
                              data-testid={`ft-task-issue-${row.date}-${t.id}`}
                            ><AlertTriangle className="w-3.5 h-3.5 mr-1" />Aksaklık</Button>
                          </div>
                        </div>
                        {!done && (
                          <div className="mt-2">
                            <Label className="text-[11px] uppercase tracking-widest text-[color:var(--y-muted)]">Sebep (zorunlu)</Label>
                            <Textarea
                              rows={2}
                              value={dr[`task_${t.id}_note`] || ""}
                              onChange={(e) => setTaskNote(row.date, t.id, e.target.value)}
                              placeholder="Aksaklığı kısaca açıkla..."
                              data-testid={`ft-task-reason-${row.date}-${t.id}`}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div>
                    <Label className="text-[11px] uppercase tracking-widest text-[color:var(--y-muted)]">Günün Notu (opsiyonel)</Label>
                    <Textarea
                      rows={2}
                      value={dr.notes || ""}
                      onChange={(e) => setDayNote(row.date, e.target.value)}
                      placeholder="Genel gün notu..."
                      data-testid={`ft-day-note-${row.date}`}
                    />
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <div className="text-[11px] text-[color:var(--y-muted)] font-mono">
                      {row.updated_at ? `Son kayıt: ${row.updated_by_name || "-"} · ${row.updated_at.slice(0, 16).replace("T", " ")}` : "Kaydedilmemiş"}
                    </div>
                    <Button
                      onClick={() => saveDay(row.date)}
                      disabled={savingDate === row.date}
                      data-testid={`ft-save-${row.date}`}
                    >
                      {savingDate === row.date ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Kaydet
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const cls = tone === "ok" ? "text-emerald-400" : tone === "warn" ? "text-orange-400" : tone === "mute" ? "text-slate-400" : "text-[color:var(--y-text)]";
  return (
    <div className="rounded-md border border-[color:var(--y-border)] p-3" style={{ background: "var(--y-surface)" }}>
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--y-muted)] font-mono">{label}</div>
      <div className={`mt-1 text-2xl font-mono font-bold ${cls}`}>{value}</div>
    </div>
  );
}
